(() => {
  if (customElements.get('bidirectional-sticky')) return;

  class BidirectionalSticky extends HTMLElement {
    constructor() {
      super();
      this.content = null;
      this.mediaQuery = window.matchMedia('(min-width: 990px)');
      this.resizeObserver = null;
      this.frame = null;
      this.lastScrollY = window.scrollY;
      this.direction = 'down';
      this.directionChanged = false;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleLayoutChange = this.handleLayoutChange.bind(this);
      this.update = this.update.bind(this);
    }

    connectedCallback() {
      this.content = this.querySelector(':scope > .product-right-col__content');

      if (!this.content || this.dataset.enabled !== 'true') return;

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleLayoutChange);
      this.mediaQuery.addEventListener('change', this.handleLayoutChange);

      this.resizeObserver = new ResizeObserver(this.handleLayoutChange);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.content);

      this.scheduleUpdate();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleLayoutChange);
      this.mediaQuery.removeEventListener('change', this.handleLayoutChange);
      this.resizeObserver?.disconnect();

      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    handleScroll() {
      const currentScrollY = window.scrollY;
      const nextDirection = currentScrollY > this.lastScrollY
        ? 'down'
        : currentScrollY < this.lastScrollY
          ? 'up'
          : this.direction;

      if (nextDirection !== this.direction) {
        this.direction = nextDirection;
        this.directionChanged = true;
      }

      this.lastScrollY = currentScrollY;
      this.scheduleUpdate();
    }

    handleLayoutChange() {
      this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.update);
    }

    update() {
      this.frame = null;

      if (!this.content || !this.mediaQuery.matches) {
        this.reset();
        return;
      }

      const styles = window.getComputedStyle(this);
      const topOffset = Number.parseFloat(styles.getPropertyValue('--sticky-sidebar-top-offset')) || 0;
      const bottomOffset = Number.parseFloat(styles.getPropertyValue('--sticky-sidebar-bottom-offset')) || 0;
      const contentHeight = this.content.offsetHeight;
      const availableHeight = window.innerHeight - topOffset - bottomOffset;

      if (contentHeight <= availableHeight) {
        this.dataset.stickyMode = 'short';
        this.removeAttribute('data-sticky-state');
        this.style.removeProperty('--sticky-sidebar-position');
        this.style.removeProperty('--sticky-sidebar-tall-top');
        this.directionChanged = false;
        return;
      }

      const maxPosition = Math.max(0, this.clientHeight - contentHeight);
      const tallTop = window.innerHeight - bottomOffset - contentHeight;
      this.style.setProperty('--sticky-sidebar-tall-top', `${tallTop}px`);

      if (this.dataset.stickyMode !== 'tall') {
        const contentBounds = this.content.getBoundingClientRect();
        const containerBounds = this.getBoundingClientRect();
        this.setMovingState(contentBounds.top - containerBounds.top, maxPosition);
        this.dataset.stickyMode = 'tall';
      } else if (
        this.directionChanged
        && (this.dataset.stickyState === 'top' || this.dataset.stickyState === 'bottom')
      ) {
        const contentBounds = this.content.getBoundingClientRect();
        const containerBounds = this.getBoundingClientRect();
        this.setMovingState(contentBounds.top - containerBounds.top, maxPosition);
      }

      this.directionChanged = false;

      if (this.dataset.stickyState !== 'moving') return;

      const contentBounds = this.content.getBoundingClientRect();

      if (this.direction === 'down' && contentBounds.bottom <= window.innerHeight - bottomOffset) {
        this.dataset.stickyState = 'bottom';
      } else if (this.direction === 'up' && contentBounds.top >= topOffset) {
        this.dataset.stickyState = 'top';
      }
    }

    setMovingState(position, maxPosition) {
      const clampedPosition = Math.min(Math.max(position, 0), maxPosition);
      this.style.setProperty('--sticky-sidebar-position', `${clampedPosition}px`);
      this.dataset.stickyState = 'moving';
    }

    reset() {
      this.removeAttribute('data-sticky-mode');
      this.removeAttribute('data-sticky-state');
      this.style.removeProperty('--sticky-sidebar-position');
      this.style.removeProperty('--sticky-sidebar-tall-top');
      this.directionChanged = false;
    }
  }

  customElements.define('bidirectional-sticky', BidirectionalSticky);
})();
